import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { useUpdateWorkspace } from '../../hooks/useWorkspace.js';
import { useFeatureFlag } from '../../lib/posthog.js';
import { toast } from '../../lib/toast.js';

const TIMEZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Rome', 'Europe/Madrid',
  'Europe/Amsterdam', 'Europe/Stockholm', 'Europe/Warsaw', 'Europe/Istanbul',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Bangkok', 'Asia/Singapore',
  'Asia/Tokyo', 'Asia/Seoul', 'Asia/Shanghai',
  'Australia/Sydney', 'Australia/Melbourne', 'Pacific/Auckland',
];

const CURRENCIES = [
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'NZD', label: 'NZD — New Zealand Dollar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
  { code: 'SEK', label: 'SEK — Swedish Krona' },
  { code: 'NOK', label: 'NOK — Norwegian Krone' },
  { code: 'DKK', label: 'DKK — Danish Krone' },
  { code: 'MXN', label: 'MXN — Mexican Peso' },
  { code: 'BRL', label: 'BRL — Brazilian Real' },
  { code: 'INR', label: 'INR — Indian Rupee' },
  { code: 'SGD', label: 'SGD — Singapore Dollar' },
  { code: 'HKD', label: 'HKD — Hong Kong Dollar' },
  { code: 'ZAR', label: 'ZAR — South African Rand' },
];

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'it', label: 'Italiano' },
  { code: 'pt', label: 'Português' },
];

interface FormState {
  name: string;
  orderNumberPrefix: string;
  bucketLabel: string;
  bucketLabelPlural: string;
  orderLabel: string;
  orderLabelPlural: string;
  timezone: string;
  currency: string;
  language: string;
}

export default function WorkspaceSettings() {
  const { t } = useTranslation('settings');
  const workspace = useWorkspaceStore((s) => s.workspace);
  const workspaceSettingsEnabled = useFeatureFlag('workspace_settings');
  const updateWorkspace = useUpdateWorkspace();

  const [form, setForm] = useState<FormState>({
    name: '',
    orderNumberPrefix: '',
    bucketLabel: '',
    bucketLabelPlural: '',
    orderLabel: '',
    orderLabelPlural: '',
    timezone: 'America/Chicago',
    currency: 'USD',
    language: 'en',
  });

  // Initialise form from workspace when loaded
  useEffect(() => {
    if (!workspace) return;
    setForm({
      name: workspace.name,
      orderNumberPrefix: workspace.orderNumberPrefix,
      bucketLabel: workspace.bucketLabel,
      bucketLabelPlural: workspace.bucketLabelPlural,
      orderLabel: workspace.orderLabel,
      orderLabelPlural: workspace.orderLabelPlural,
      timezone: workspace.timezone,
      currency: workspace.currency,
      language: workspace.language,
    });
  }, [workspace?.id]);

  function set(key: keyof FormState, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    try {
      await updateWorkspace.mutateAsync({
        name: form.name.trim() || undefined,
        orderNumberPrefix: form.orderNumberPrefix.trim() || undefined,
        bucketLabel: form.bucketLabel.trim() || undefined,
        bucketLabelPlural: form.bucketLabelPlural.trim() || undefined,
        orderLabel: form.orderLabel.trim() || undefined,
        orderLabelPlural: form.orderLabelPlural.trim() || undefined,
        timezone: form.timezone || undefined,
        currency: form.currency || undefined,
        language: form.language || undefined,
      });
      toast.success(t('workspace.saved'));
    } catch {
      toast.error(t('workspace.save_error'));
    }
  }

  if (!workspaceSettingsEnabled) {
    return (
      <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>
        Workspace settings are not enabled for your account.
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
          {t('workspace.title')}
        </h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {t('workspace.subtitle')}
        </p>
      </div>

      <form onSubmit={(e) => void handleSave(e)} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>

        {/* General */}
        <section style={sectionStyle}>
          <h3 style={sectionHeadingStyle}>{t('workspace.section_general')}</h3>
          <div style={gridStyle}>
            <Field label={t('workspace.name_label')}>
              <input
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder={t('workspace.name_placeholder')}
                required
                style={inputStyle}
              />
            </Field>
            <Field label={t('workspace.order_prefix_label')} hint={t('workspace.order_prefix_hint', { prefix: form.orderNumberPrefix || 'ORD' })}>
              <input
                value={form.orderNumberPrefix}
                onChange={(e) => set('orderNumberPrefix', e.target.value.toUpperCase())}
                placeholder={t('workspace.order_prefix_placeholder')}
                maxLength={10}
                style={{ ...inputStyle, textTransform: 'uppercase' }}
              />
            </Field>
          </div>
        </section>

        {/* Labels */}
        <section style={sectionStyle}>
          <h3 style={sectionHeadingStyle}>{t('workspace.section_labels')}</h3>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>
            Rename the "Project" and "Order" concepts to match your business (e.g. Job / Quote / Job).
          </p>
          <div style={gridStyle}>
            <Field label={t('workspace.bucket_label_label', { bucket_label: 'Project' })}>
              <input value={form.bucketLabel} onChange={(e) => set('bucketLabel', e.target.value)} required style={inputStyle} />
            </Field>
            <Field label={t('workspace.bucket_label_plural_label', { bucket_label: 'Project' })}>
              <input value={form.bucketLabelPlural} onChange={(e) => set('bucketLabelPlural', e.target.value)} required style={inputStyle} />
            </Field>
            <Field label={t('workspace.order_label_label', { order_label: 'Order' })}>
              <input value={form.orderLabel} onChange={(e) => set('orderLabel', e.target.value)} required style={inputStyle} />
            </Field>
            <Field label={t('workspace.order_label_plural_label', { order_label: 'Order' })}>
              <input value={form.orderLabelPlural} onChange={(e) => set('orderLabelPlural', e.target.value)} required style={inputStyle} />
            </Field>
          </div>
        </section>

        {/* Regional */}
        <section style={sectionStyle}>
          <h3 style={sectionHeadingStyle}>{t('workspace.section_regional')}</h3>
          <div style={gridStyle}>
            <Field label={t('workspace.timezone_label')}>
              <select value={form.timezone} onChange={(e) => set('timezone', e.target.value)} style={inputStyle}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>
                ))}
              </select>
            </Field>
            <Field label={t('workspace.currency_label')}>
              <select value={form.currency} onChange={(e) => set('currency', e.target.value)} style={inputStyle}>
                {CURRENCIES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </Field>
            <Field label={t('workspace.language_label')}>
              <select value={form.language} onChange={(e) => set('language', e.target.value)} style={inputStyle}>
                {LANGUAGES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </Field>
          </div>
        </section>

        <div>
          <button
            type="submit"
            disabled={updateWorkspace.isPending}
            style={{
              padding: 'var(--space-2) var(--space-5)',
              background: 'var(--color-brand)', color: 'var(--color-text-inverse)',
              border: 'none', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
              cursor: updateWorkspace.isPending ? 'default' : 'pointer',
              opacity: updateWorkspace.isPending ? 0.7 : 1,
            }}
          >
            {updateWorkspace.isPending ? '…' : t('workspace.save_button')}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <label style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text-secondary)' }}>
        {label}
      </label>
      {children}
      {hint && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</p>
      )}
    </div>
  );
}

const sectionStyle: React.CSSProperties = {
  background: 'var(--color-bg)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
};

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-semibold)',
  color: 'var(--color-text)',
  marginBottom: 'var(--space-4)',
  paddingBottom: 'var(--space-3)',
  borderBottom: '1px solid var(--color-border)',
};

const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
  gap: 'var(--space-4)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--space-2) var(--space-3)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-sm)',
  color: 'var(--color-text)',
  background: 'var(--color-bg)',
  boxSizing: 'border-box',
};
